import { IcdDiagnosisCodeResponse } from '@/store/slices/api/diagnosisCodesApiSlice';

import { Billing } from '@/types/billing.ts';
import { RegisteredNpi } from '@/types/npi.ts';
import { VtTransmission } from '@/types/vendorTransmissionReport.ts';

import { MatchDevice } from './matchDevice';
import { Address, Organization } from './organization';
import { Practitioner } from './reportsPatient';
import {
  ReportColor,
  ReportType,
  SignatureStatus,
  TransmissionReportFilesUploadStatus
} from './transmissionReport';
import { User } from './user';

export type PatientTransmissionReport = {
  color: ReportColor;
  createdAt: string;
  id: number;
  reportName: string;
  showOrderStatus: boolean;
  orderStatus: string;
  signatureStatus: SignatureStatus;
  updatedAt: string;
  editedUser: string;
  editedDate: string;
  transmissionType: ReportType;
  filesUploadStatus?: TransmissionReportFilesUploadStatus;
  reportType?: string;
  flagHeartFailure: boolean;
  vtTransmissionDate?: string;
  matchDevice?: MatchDevice;
  billing?: Billing;
  isPendingReview: boolean;
  PDFUploadKey?: string;
};

export type ViewAndDownloadReport = {
  id: number;
  PDFUploadKey?: string;
  signatureStatus: SignatureStatus;
  transmissionType: ReportType;
  reportType?: string;
  flagHeartFailure: boolean;
  createdAt: string;
};

export interface Patient {
  id: string;
  mrn1: string;
  mrn2: string;
  familyName: string;
  givenName: string;
  preferredName: string;
  birthDate: string;
  sex: string;
  email: string;
  phoneNumber: string;
  otherPhone: string;
  emergencyName: string;
  emergencyPhone: string;
  patientStatus: string;
  patientNotes: string;
  homePhone: string;
  referralSource: string;
  ssn: string;
  status: PatientStatusEnum;
  createdAt: string;
  updatedAt: string;
  address: Address;
  clinic: Organization;
  matchDevices: Array<MatchDevice>;
  rhythmId: string;
  oac: string;
  referringPhysician: string;
  referringProvider: RegisteredNpi;
  implantingEp: RegisteredNpi;
  implantingEpFreeText: string;
  monitorReason: string;
  diagnosisOption: string;
  practitioner: User;
  files: Array<any>;
  recentReportColor: ReportColor;
  recentReportType: string;
  recentReportDate: Date;
  recentReportId: number;
  recentReportDeviceType: string;
  recentReportReportType?: string;
  recentHeartFailureStatus: boolean;
  count: number;
  reportData: ReportData[];
  insuranceCarrier: string;
  statusReason: string | null;
  statusStartDate?: string | Date | null;
  statusEndDate?: string | Date | null;
  statusNotes?: string;
  transmissionReports?: PatientTransmissionReport[];
  vtTransmissionReports?: VtTransmission[];
  icdDiagnosis: IcdDiagnosisCodeResponse;
  inClinicPractitioner: Practitioner;
  oacUpdatedAt?: Date;
  oacUpdatedByUser?: User;
  salesforceRemoteId?: string;
  statusOtherReason: string | null;
}

export interface ReportData {
  t_color: string;
  id: number;
  patientid: string;
  transmissiondate: string;
  givenname: string;
  familyname: string;

  transmissiontype: string;
  reporttype?: string;
  downloaded: boolean;
  downloaddate: string | null;
  flagheartfailure: boolean;
  pdfuploadkey: string;
  pdfuploadnotes: string;
  device_type: string;
  reportFileName: string;
}

export interface MiniAppointment {
  id: string;
  type: string | null;
  frequency: string | null;
  status: string | null;
}

export interface MiniPatientDevice {
  deviceId: string;
  deviceType: string;
  modelName: string;
  status: string;
  frequency: string;
  appointments: MiniAppointment[] | null;
  nextAppointmentId: string;
  nextScheduledDate: string;
}

export interface MiniPatient {
  id: string;
  patientStatus: string;
  familyName: string;
  givenName: string;
  mrn2: string;
  birthDate?: Date;
  monitorReason: string;
  sex: string;
  statusReason: string;
  statusNotes: string;
  diagnosisOption: string;
  oac: string;
  practitioner: {
    id: string;
    family: string;
    given: string;
  };
  referringProvider: {
    firstName: string;
    lastName: string;
    NPI: string;
  };
  referringPhysician: string;
  clinic: {
    id: string;
    name: string;
    isManagedInRS: boolean;
    parent: {
      name: string;
      ehrIntegration: boolean;
      isManagedInRS: boolean;
      saveCombinedPDF: boolean;
    };
  };
  rhythmId: string;
  patientNotes: string;
  devices?: MiniPatientDevice[];
  /*The insurance carrier field is not present in the backend and not being used at the moment.*/
  insuranceCarrier?: string | null;
}

/*This is for the old patient page, will remove it once we move to the new patient profile page.*/
export interface NextScheduleTransmission {
  deviceId: string;
  deviceType: string;
  LastScheduledDate: string;
  nextScheduledDate: string;
  transmissionType: string;
  scheduleFrequency: number;
}

/*This is for the old patient page, will remove it once we move to the new patient profile page.*/
export interface PatientsNextScheduleTransmission {
  patientId: string;
  nextScheduleTranmission: Array<NextScheduleTransmission>;
}

export enum PatientStatusEnum {
  Draft = 'draft',
  Saved = 'saved',
  InsuranceVerified = 'insurance_verified',
  PendingPof = 'pending_pof',
  Scheduled = 'scheduled'
}

export enum PatientActiveStatusEnum {
  All = 'All',
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Pending = 'PENDING',
  Paused = 'PAUSED',
  New = 'NEW',
  InProgress = 'IN PROGRESS',
  Closed = 'CLOSED',
  Candidate = 'CANDIDATE'
}

export enum PatientOacEnum {
  'No Current OAC' = 'No Current OAC',
  'On OAC [Unspecified Type]' = 'On OAC [Unspecified Type]',
  'Watchman / LAAOD' = 'Watchman / LAAOD',
  'Unable to Tolerate' = 'Unable to Tolerate',
  'Patient Refused' = 'Patient Refused',
  'Low CHA₂DS₂-VASc / Not Indicated' = 'Low CHA₂DS₂-VASc / Not Indicated',
  'Apixaban (Eliquis)' = 'Apixaban (Eliquis)',
  'Apixaban (Eliquis) 2.5mg' = 'Apixaban (Eliquis) 2.5mg',
  'Apixaban (Eliquis) 5mg' = 'Apixaban (Eliquis) 5mg',
  'Rivaroxaban (Xarelto)' = 'Rivaroxaban (Xarelto)',
  'Rivaroxaban (Xarelto) 2.5mg' = 'Rivaroxaban (Xarelto) 2.5mg',
  'Rivaroxaban (Xarelto) 10mg' = 'Rivaroxaban (Xarelto) 10mg',
  'Rivaroxaban (Xarelto) 15mg' = 'Rivaroxaban (Xarelto) 15mg',
  'Rivaroxaban (Xarelto) 20mg' = 'Rivaroxaban (Xarelto) 20mg',
  'Dabigatran (Pradaxa)' = 'Dabigatran (Pradaxa)',
  'Dabigatran (Pradaxa) 75mg' = 'Dabigatran (Pradaxa) 75mg',
  'Dabigatran (Pradaxa) 110mg' = 'Dabigatran (Pradaxa) 110mg',
  'Dabigatran (Pradaxa) 150mg' = 'Dabigatran (Pradaxa) 150mg',
  'Edoxaban (Savaysa)' = 'Edoxaban (Savaysa)',
  'Edoxaban (Savaysa) 15mg' = 'Edoxaban (Savaysa) 15mg',
  'Edoxaban (Savaysa) 30mg' = 'Edoxaban (Savaysa) 30mg',
  'Edoxaban (Savaysa) 60mg' = 'Edoxaban (Savaysa) 60mg',
  'Warfarin (Coumadin)' = 'Warfarin (Coumadin)',
  'Warfarin (Coumadin) 1mg' = 'Warfarin (Coumadin) 1mg',
  'Warfarin (Coumadin) 2mg' = 'Warfarin (Coumadin) 2mg',
  'Warfarin (Coumadin) 2.5mg' = 'Warfarin (Coumadin) 2.5mg',
  'Warfarin (Coumadin) 3mg' = 'Warfarin (Coumadin) 3mg',
  'Warfarin (Coumadin) 4mg' = 'Warfarin (Coumadin) 4mg',
  'Warfarin (Coumadin) 5mg' = 'Warfarin (Coumadin) 5mg',
  'Warfarin (Coumadin) 6mg' = 'Warfarin (Coumadin) 6mg',
  'Warfarin (Coumadin) 7.5mg' = 'Warfarin (Coumadin) 7.5mg',
  'Warfarin (Coumadin) 10mg' = 'Warfarin (Coumadin) 10mg',
  Yes = 'Yes',
  No = 'No',
  NA = 'N/A',
  NotTolerated = 'Not Tolerated',
  PatientDeclined = 'Patient Declined',
  HOLAAClosureDevice = 'H/o LAA Closure Device',
  Contraindicated = 'Contraindicated',
  'Fall Risk' = 'Fall Risk',
  'Aspirin Only' = 'Aspirin Only'
}

export enum PatientMonitoringReason {
  AFManagement = 'AF Management',
  Bradycardia = 'Bradycardia',
  CardiacArrest = 'Cardiac Arrest',
  CompleteHeartBlock = 'Complete Heart Block',
  CryptogenicStrokeTIA = 'Cryptogenic Stroke/TIA',
  HeartFailureUnspecified = 'Heart Failure Unspecified',
  HighGradeAVBlock = 'High Grade AV Block',
  Palpitations = 'Palpitations',
  PrimaryPreventionSCD = 'Primary Prevention of SCD',
  SecondaryPreventionSCD = 'Secondary Prevention of SCD',
  SickSinusSyndrome = 'Sick Sinus Syndrome',
  SuspectedAF = 'Suspected AF',
  Syncope = 'Syncope',
  TachyBradySyndrome = 'Tachy-Brady Syndrome',
  VentricularTachycardia = 'Ventricular Tachycardia'
}

export interface Clinic {
  id: string;
  name: string;
}

export interface Device {
  id: string;
  vendor: string;
  serialNumber: string;
  type: string;
  deviceImplantDate: string;
  modelName: string;
}

export interface PatientsResponse {
  patients: Patient[];
  total: number;
  totalNotDownloadedCount: string;
  currentOffset: number;

  //ad-hoc non-generated additional fields (will come in next generation)
  transmissionScheduleReadOnly: boolean;
}

export interface GetAllPatientsRequestParams {
  skip?: number;
  pageLimit?: number;
  isNew?: boolean;
  search?: string;
  sortBy?: string;
  sortDirection?: string;
  isRecentSigned?: boolean;
  isDownloaded?: boolean;
  deviceSchedule?: string;
  status?: string;
  clinicIds?: string[];
  practitionersIds?: string[];
  deviceTypes?: string[];
  vendors?: string[];
  patientStatuses?: string[];
  forV2?: boolean;
}

export interface PatientMonitoredData {
  year: number;
  month: number;
  count: number;
}

export interface GetTotalPatientsMonitoredRequest {
  organizationIds?: string[];
  startFrom?: Date;
  endAt?: Date;
  deviceTypes?: string[];
  practitionerIds?: string[];
  signingPractitionerIds?: string[];
}

export interface GetTotalPatientsMonitoredResponse {
  startFrom: string;
  endAt: string;
  patientsData: Array<PatientMonitoredData>;
}

export interface FindAllAuthorizedPractitionersResponse {
  practitioners: Array<User>;
}

export interface FindAllInClinicPractitionersResponse {
  practitioners: Array<User>;
}

export interface PatientDiagnosis {
  id: string;
  salesforceRemoteId: string | null;
  createdAt: string;
  updatedAt: string;
  patientId: string;
  diagnosisCode: string;
  description: string;
  isPrimary: boolean;
  clinicalStatus: string;
  verificationStatus: string;
  onsetDate: string | null;
  recordedDate: string | null;
  resolvedDate: string | null;
  createdBy: string;
  updatedBy: string;
  deletedAt: string | null;
}

export interface PatientDiagnosesResponse {
  data: PatientDiagnosis[];
  total: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface GetPatientDiagnosesParams {
  patientId: string;
  isPrimary?: boolean;
  clinicalStatus?: number;
  verificationStatus?: number;
  limit?: number;
  page?: number;
}

export interface UpdatePatientDto {
  patient: Partial<Patient>;
  // this is for updating cache of report if patient details were updated from report page
  reportId?: number;
  activeReportId?: number;
}

export interface PatientSettings {
  patientId: string;
  hfMonitoring: boolean;
  hfOnlyMonitoring: boolean;
  alertOnlyMonitoring: boolean;
  cycleIcdPm: number;
  cycleHf: number;
  cycleIlr: number;
  inClinicBuffer: number;
}

export interface PatientUpdateOverviewDetails {
  givenName?: string;
  familyName?: string;
  preferredName?: string;
  sex?: string;
  address?: {
    id?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  phoneNumber?: string;
  homePhone?: string;
  otherPhone?: string;
  mrn2?: string;
  birthDate?: string;
}

export interface ScheduleDevice {
  id: string;
  vendor: string;
  type: string;
}

export interface PatientSchedule {
  id: string;
  frequencyNumber: number;
  status: string;
  type: string;
  device: ScheduleDevice;
  scheduledDate: string;
  isReceived?: boolean;
  transmissionReportId?: number;
}

export interface GetPatientSchedulesRequest {
  status?: 'completed' | 'scheduled' | 'no show';
  limit?: number;
  page?: number;
}

export interface GetPatientSchedulesResponse {
  data: PatientSchedule[];
  limit: number;
  page: number;
  total: number;
  totalPages: number;
}
